import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { CONTAINERS as API_CONTAINERS } from './ContainerPagination';
import { NOTIFICATION_CONTAINERS } from './NotificationPageLayout';
import { TROUBLESHOOTING_CONTAINERS } from './TroubleshootingPageLayout';
import { APPENDIX_CONTAINERS } from './AppendixPageLayout';

const SECTION_CONFIG = {
  'api-reference': {
    label: 'API Reference',
    path: '/technical-documentation/api-reference',
    items: API_CONTAINERS.map((c) => ({ id: c.id, title: c.title, path: c.link, built: c.built })),
  },
  notifications: {
    label: 'Notifications',
    path: '/technical-documentation/notifications',
    items: NOTIFICATION_CONTAINERS.map((c) => ({ id: c.id, title: c.title, path: c.link, built: true })),
  },
  troubleshooting: {
    label: 'Troubleshooting',
    path: '/technical-documentation/troubleshooting',
    items: TROUBLESHOOTING_CONTAINERS.map((c) => ({ id: c.id, title: c.title, path: c.link, built: true })),
  },
  appendix: {
    label: 'Appendix',
    path: '/technical-documentation/appendix',
    items: APPENDIX_CONTAINERS.map((c) => ({ id: c.id, title: c.title, path: c.link, built: true })),
  },
};

/** Returns true when the active element is an editable field — used to skip shortcuts while typing. */
const isTypingContext = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Compact breadcrumb shown at the top of every documentation container page.
 *
 * Keyboard shortcuts (GitHub-style):
 *   g d       → open the "jump to" dropdown
 *   ↑ / ↓     → move highlight inside dropdown
 *   Enter     → navigate to highlighted page
 *   Esc       → close the dropdown
 */
const DocsBreadcrumb = ({ section, currentId }) => {
  const cfg = SECTION_CONFIG[section];
  const items = useMemo(() => cfg?.items?.filter((i) => i.built !== false) || [], [cfg]);

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // --- Click-outside to close ---
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [open]);

  // --- Opens the dropdown, seeds highlight on the current page ---
  const openDropdown = useCallback(() => {
    const idx = items.findIndex((i) => i.id === currentId);
    setHighlight(idx >= 0 ? idx : 0);
    setOpen(true);
  }, [items, currentId]);

  // --- Global "g then d" shortcut ---
  useEffect(() => {
    let gPressedAt = 0;
    const handleGlobalKey = (e) => {
      if (isTypingContext()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const now = Date.now();
      if (e.key.toLowerCase() === 'g') {
        gPressedAt = now;
        return;
      }
      if (e.key.toLowerCase() === 'd' && now - gPressedAt < 1200) {
        gPressedAt = 0;
        e.preventDefault();
        openDropdown();
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [openDropdown]);

  // --- Arrow-key navigation inside the dropdown ---
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % items.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (h - 1 + items.length) % items.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const target = items[highlight];
        if (target) {
          setOpen(false);
          navigate(target.path);
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, items, highlight, navigate]);

  // --- Scroll the highlighted item into view ---
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-highlight-index="${highlight}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  if (!cfg) return null;
  const current = cfg.items.find((i) => i.id === currentId);

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center flex-wrap gap-1 text-sm mb-5"
      data-testid="docs-breadcrumb"
    >
      <Link
        to="/technical-documentation"
        className="text-gray-500 hover:text-[#1F4E79] transition-colors font-medium"
        data-testid="breadcrumb-root"
      >
        Technical Documentation
      </Link>
      <ChevronRight size={14} className="text-gray-400" />

      <Link
        to={cfg.path}
        className="text-gray-500 hover:text-[#1F4E79] transition-colors font-medium"
        data-testid="breadcrumb-section"
      >
        {cfg.label}
      </Link>
      <ChevronRight size={14} className="text-gray-400" />

      {/* Current page with sibling-dropdown */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openDropdown())}
          aria-expanded={open}
          aria-haspopup="true"
          className="inline-flex items-center gap-1.5 text-[#1F4E79] font-semibold hover:text-[#163a5f] transition-colors cursor-pointer group"
          data-testid="breadcrumb-current"
        >
          {current?.title || currentId}
          <ChevronDown size={14} className={`text-[#1F4E79] transition-transform ${open ? 'rotate-180' : ''}`} />
          <span
            className="hidden md:inline-flex items-center gap-1 ml-1 text-[9px] font-mono font-normal text-gray-400 group-hover:text-gray-600 transition-colors"
            aria-hidden="true"
          >
            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-gray-600 font-sans">g</kbd>
            <span className="text-gray-300">then</span>
            <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-gray-600 font-sans">d</kbd>
          </span>
        </button>

        {open && (
          <div
            className="absolute left-0 top-full mt-2 w-72 bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-200 overflow-hidden z-40"
            data-testid="breadcrumb-dropdown"
          >
            <div className="px-4 py-2 bg-[#1F4E79] text-white text-xs font-semibold uppercase tracking-wider">
              Jump to {cfg.label}
            </div>
            <ul className="max-h-80 overflow-y-auto py-1" ref={listRef}>
              {cfg.items.map((item) => {
                const idx = items.findIndex((i) => i.id === item.id);
                const active = item.id === currentId;
                const highlighted = idx === highlight && idx >= 0;
                const soon = item.built === false;
                return (
                  <li key={item.id}>
                    {soon ? (
                      <span className="flex items-center justify-between px-4 py-2 text-xs text-gray-400 cursor-not-allowed italic">
                        {item.title}
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[9px] font-bold uppercase">Soon</span>
                      </span>
                    ) : (
                      <Link
                        to={item.path}
                        data-highlight-index={idx}
                        onClick={() => setOpen(false)}
                        onMouseEnter={() => setHighlight(idx)}
                        className={`block px-4 py-2 text-xs transition-colors border-l-2 ${
                          highlighted
                            ? 'border-[#ef5b25] bg-orange-50 text-[#1F4E79] font-semibold'
                            : active
                            ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                            : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                        }`}
                        data-testid={`breadcrumb-dropdown-item-${item.id}`}
                      >
                        {item.title}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {/* Keyboard hint footer */}
            <div className="flex items-center justify-between gap-3 px-4 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-500">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-300 rounded font-sans">↑</kbd>
                <kbd className="px-1 py-0.5 bg-white border border-gray-300 rounded font-sans">↓</kbd>
                <span>navigate</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-300 rounded font-sans">Enter</kbd>
                <span>select</span>
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-white border border-gray-300 rounded font-sans">Esc</kbd>
                <span>close</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default DocsBreadcrumb;
